/**
 * The nine languages, and why there is no region here.
 *
 * The sites that sell something carry sixteen *locales* — a language plus a
 * region, because the region picks a currency and a tax line. Relay sells
 * nothing and has no price to write, so a region would buy sixteen catalogues
 * where nine say the same words. The desktop app made the same call for the
 * same reason; this is the browser half of it.
 *
 * English is the source and the fallback everywhere. A key with no translation
 * renders readable, not blank.
 */

export const LANGUAGES = [
  { tag: 'en', intl: 'en-GB', label: 'English' },
  { tag: 'de', intl: 'de-DE', label: 'Deutsch' },
  { tag: 'es', intl: 'es-ES', label: 'Español' },
  { tag: 'fr', intl: 'fr-FR', label: 'Français' },
  { tag: 'it', intl: 'it-IT', label: 'Italiano' },
  { tag: 'ja', intl: 'ja-JP', label: '日本語' },
  { tag: 'nl', intl: 'nl-NL', label: 'Nederlands' },
  { tag: 'pt', intl: 'pt-PT', label: 'Português' },
  { tag: 'tr', intl: 'tr-TR', label: 'Türkçe' }
] as const;

export type Language = (typeof LANGUAGES)[number]['tag'];

export const DEFAULT_LANGUAGE: Language = 'en';

export function isLanguage(tag: string): tag is Language {
  return LANGUAGES.some((l) => l.tag === tag);
}

export function languageOf(tag: string) {
  return LANGUAGES.find((l) => l.tag === tag) ?? LANGUAGES[0];
}

const KEY = 'mauvely.language';

/**
 * The language to read in: a saved choice, then the browser's, then English.
 *
 * `navigator.language` is `de-AT` or `pt-BR`; only the first segment is
 * consulted, because that is the whole of what this app varies by.
 */
export function currentLanguage(): Language {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && isLanguage(saved)) return saved;
  } catch {
    // Private mode, or storage blocked. The browser's preference still works.
  }
  const first = (navigator.language ?? '').split('-')[0] ?? '';
  return isLanguage(first) ? first : DEFAULT_LANGUAGE;
}

export function setLanguage(tag: Language): void {
  try {
    localStorage.setItem(KEY, tag);
  } catch {
    // Nothing to do — the choice lasts this page instead.
  }
}
